'use client';

import * as React from 'react';
import { cn } from '../lib/cn';

export interface MessageListProps {
  children: React.ReactNode;
  className?: string;
  /** Applied to the inner content wrapper (e.g. centered reading column). */
  contentClassName?: string;
}

const NEAR = 60; // px within the bottom counts as "at bottom"

/**
 * Scrollable message region that sticks to the tail while streaming, releases
 * when the user scrolls up, and shows a jump-to-bottom button when detached.
 */
export function MessageList({ children, className, contentClassName }: MessageListProps) {
  const scRef = React.useRef<HTMLDivElement | null>(null);
  const stickRef = React.useRef(true);
  const prevLenRef = React.useRef(0);
  const [showJump, setShowJump] = React.useState(false);
  const count = React.Children.count(children);

  const onScroll = React.useCallback(() => {
    const el = scRef.current;
    if (!el) return;
    const dist = el.scrollHeight - el.scrollTop - el.clientHeight;
    const atBottom = dist <= NEAR;
    stickRef.current = atBottom;
    setShowJump(!atBottom);
  }, []);

  const jump = React.useCallback(() => {
    const el = scRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
    stickRef.current = true;
    setShowJump(false);
  }, []);

  React.useLayoutEffect(() => {
    const el = scRef.current;
    if (!el) return;
    const grew = count > prevLenRef.current;
    prevLenRef.current = count;
    if (grew) {
      stickRef.current = true;
      setShowJump(false);
    }
    if (stickRef.current) {
      el.scrollTop = el.scrollHeight;
    } else {
      const dist = el.scrollHeight - el.scrollTop - el.clientHeight;
      if (dist > NEAR) setShowJump(true);
    }
  });

  return (
    <div className="relative flex min-h-0 flex-1 flex-col">
      <div
        ref={scRef}
        onScroll={onScroll}
        className={cn('flex flex-1 flex-col overflow-y-auto bg-[var(--color-chat-canvas)]', className)}
      >
        <div className={cn('flex flex-1 flex-col gap-[var(--bubble-gap)] p-4', contentClassName)}>
          {children}
        </div>
      </div>
      {showJump ? (
        <button
          type="button"
          onClick={jump}
          aria-label="맨 아래로"
          className="absolute bottom-3 left-1/2 flex size-8 -translate-x-1/2 items-center justify-center rounded-full border border-border bg-surface text-fg shadow-popover"
        >
          ↓
        </button>
      ) : null}
    </div>
  );
}
